<template>
  <div class="sb-landing-version">
    <LandingPageOriginal v-if="showOriginal" />
    <v-app v-else>
      <v-main class="landingpage">
        <article
          class="sb-public-landing"
          :class="{ 'sb-public-landing--dark': $vuetify.theme.dark }"
          lang="en"
          aria-labelledby="sb-landing-title"
        >
          <section class="sb-hero">
            <video
              ref="heroVideo"
              class="sb-hero-video"
              muted
              autoplay
              loop
              playsinline
              preload="metadata"
              aria-hidden="true"
            >
              <source
                :src="require('@/assets/images/pages/landingpage/globe.mp4')"
                type="video/mp4"
              />
            </video>
            <div class="sb-hero-shade"></div>
            <div class="sb-landing-container sb-hero-content">
              <div class="sb-hero-brand">
                <img
                  :src="require('@/assets/images/logos/selfbio-logo-6.png')"
                  alt=""
                  width="56"
                  height="56"
                />
                <span><span>SELF</span>BIOGRAPHY</span>
              </div>
              <h1 id="sb-landing-title">
                Record Your Life.<br />Tell Your Story.<br />Leave Your Mark on the World.
              </h1>
              <p class="sb-hero-intro">
                Everyone has a story worth preserving—for themselves, the people they love, and generations still to come. Build your biography one memory at a time.
              </p>
              <p class="sb-hero-puzzle">
                Every memory is a piece. Your life is the puzzle.
              </p>
              <div class="sb-hero-actions">
                <router-link
                  class="sb-landing-button sb-landing-button--primary"
                  :to="joinTarget"
                >
                  Join free
                </router-link>
                <router-link
                  class="sb-landing-button sb-landing-button--hero"
                  to="/pages/pricing#choose-plan"
                >
                  Subscribe
                </router-link>
                <router-link
                  class="sb-landing-button sb-landing-button--hero"
                  to="/pages/affiliate-program"
                >
                  Earn
                </router-link>
              </div>
              <p class="sb-hero-note">
                Discover Self Biography and start your Life Puzzle. Subscriptions from {{ startingPrice }}/month.
              </p>
            </div>
            <button
              class="sb-motion-control"
              type="button"
              :aria-label="motionPaused ? 'Play globe animation' : 'Pause globe animation'"
              @click="toggleMotion"
            >
              {{ motionPaused ? 'Play animation' : 'Pause animation' }}
            </button>
          </section>

          <section
            class="sb-landing-section"
            aria-labelledby="sb-devices-title"
          >
            <div class="sb-landing-container sb-devices-layout">
              <div>
                <p class="sb-eyebrow">
                  Across devices
                </p>
                <h2 id="sb-devices-title">
                  Anytime, anywhere.
                </h2>
                <p>Your life, your memories, and your life’s work—all in one place, organized chronologically and just one click away on any device.</p>
                <ul
                  class="sb-device-list"
                  aria-label="Devices and platforms"
                >
                  <li
                    v-for="device in content.devices"
                    :key="device"
                  >
                    {{ device }}
                  </li>
                </ul>
              </div>
              <img
                class="sb-device-image"
                :src="require('@/assets/images/pages/landing-laptop.webp')"
                alt="Self Biography shown on a laptop"
                loading="lazy"
                width="900"
                height="600"
              />
            </div>
          </section>

          <section
            class="sb-landing-section sb-landing-section--tint"
            aria-labelledby="sb-benefits-title"
          >
            <div class="sb-landing-container">
              <p class="sb-eyebrow">
                Your story, your way
              </p>
              <h2 id="sb-benefits-title">
                A place for every piece of your life.
              </h2>
              <div class="sb-benefits-grid">
                <section
                  v-for="(benefit, index) in content.benefits"
                  :key="benefit.title"
                  class="sb-benefit"
                >
                  <span
                    class="sb-section-number"
                    aria-hidden="true"
                  >0{{ index + 1 }}</span>
                  <h3>{{ benefit.title }}</h3>
                  <p>{{ benefit.text }}</p>
                </section>
              </div>
            </div>
          </section>

          <section
            class="sb-landing-section"
            aria-labelledby="sb-discover-title"
          >
            <div class="sb-landing-container">
              <p class="sb-eyebrow">
                More than memories
              </p>
              <h2 id="sb-discover-title">
                The person. The work. The life behind both.
              </h2>
              <div
                class="sb-story-tabs"
                role="tablist"
                aria-label="Discover Self Biography"
              >
                <button
                  v-for="(story, index) in content.stories"
                  :id="`sb-story-tab-${story.id}`"
                  :key="story.id"
                  ref="storyTabs"
                  class="sb-story-tab"
                  type="button"
                  role="tab"
                  :aria-selected="activeStory === index ? 'true' : 'false'"
                  :aria-controls="`sb-story-panel-${story.id}`"
                  :tabindex="activeStory === index ? 0 : -1"
                  @click="activeStory = index"
                  @keydown="onStoryKey($event, index)"
                >
                  {{ story.label }}
                </button>
              </div>
              <section
                v-for="(story, index) in content.stories"
                v-show="activeStory === index"
                :id="`sb-story-panel-${story.id}`"
                :key="story.id"
                class="sb-story-panel"
                role="tabpanel"
                :aria-labelledby="`sb-story-tab-${story.id}`"
                tabindex="0"
              >
                <div class="sb-story-copy">
                  <h3>{{ story.title }}</h3>
                  <p
                    v-for="paragraph in story.paragraphs"
                    :key="paragraph"
                  >
                    {{ paragraph }}
                  </p>
                  <details
                    v-if="story.more"
                    class="sb-reading-more"
                  >
                    <summary>{{ story.more.title }}</summary>
                    <p
                      v-for="paragraph in story.more.paragraphs"
                      :key="paragraph"
                    >
                      {{ paragraph }}
                    </p>
                  </details>
                  <router-link
                    v-if="story.link"
                    class="sb-text-link"
                    :to="story.link.to"
                  >
                    {{ story.link.label }} →
                  </router-link>
                </div>
                <img
                  class="sb-story-image"
                  :src="require(`@/assets/images/pages/landingpage/${story.image}`)"
                  :alt="story.alt"
                  loading="lazy"
                  width="720"
                  height="520"
                />
              </section>
            </div>
          </section>

          <section
            class="sb-landing-section sb-landing-section--tint"
            aria-labelledby="sb-features-title"
          >
            <div class="sb-landing-container">
              <p class="sb-eyebrow">
                Current features
              </p>
              <h2 id="sb-features-title">
                Start preserving your story today.
              </h2>
              <p class="sb-section-intro">
                Bring the people, places, photographs, accomplishments, and everyday moments of your life together. Open a feature to learn more.
              </p>
              <div class="sb-features-grid">
                <details
                  v-for="feature in content.features"
                  :key="feature.title"
                  class="sb-feature"
                >
                  <summary>{{ feature.title }}<span aria-hidden="true">+</span></summary>
                  <p>{{ feature.text }}</p>
                </details>
              </div>
              <router-link
                class="sb-text-link"
                to="/pages/documentation"
              >
                Explore the documentation →
              </router-link>
            </div>
          </section>

          <section
            id="life-puzzle"
            class="sb-landing-section"
            aria-labelledby="sb-puzzle-title"
          >
            <div class="sb-landing-container sb-puzzle-layout">
              <div
                class="sb-puzzle-picture"
                aria-hidden="true"
              >
                <div class="sb-puzzle-piece">
                  <img
                    :src="require('@/assets/images/pages/landingpage/2.webp')"
                    alt=""
                    loading="lazy"
                  /><span>Memories</span>
                </div>
                <div class="sb-puzzle-piece">
                  <img
                    :src="require('@/assets/images/pages/landingpage/10.webp')"
                    alt=""
                    loading="lazy"
                  /><span>Life’s work</span>
                </div>
                <div class="sb-puzzle-piece">
                  <img
                    :src="require('@/assets/images/pages/landingpage/1.webp')"
                    alt=""
                    loading="lazy"
                  /><span>Connections</span>
                </div>
                <div class="sb-puzzle-piece">
                  <img
                    :src="require('@/assets/images/pages/landingpage/15.webp')"
                    alt=""
                    loading="lazy"
                  /><span>Experiences</span>
                </div>
              </div>
              <div>
                <p class="sb-eyebrow">
                  The Life Puzzle
                </p>
                <h2 id="sb-puzzle-title">
                  Every memory is a piece.<br />Your life is the puzzle.
                </h2>
                <p>Start anywhere—in childhood, yesterday, your travels, or your career. Connect photographs, dates, people, and places. Ask others about missing pieces and watch your biography take shape.</p>
                <p class="sb-puzzle-statement">
                  There is no predetermined picture on the box. The picture is you.
                </p>
                <details class="sb-reading-more sb-game-details">
                  <summary>Discover the game</summary>
                  <p class="sb-status-label">
                    Game experience in development
                  </p>
                  <h3>The Life Puzzle Game</h3>
                  <p>Turn memories into Memory Bits—the individual tiles of your Life Puzzle. Each tile can hold photographs, stories, videos, dates, places, people, and shared experiences. The proposed game experience builds on these pieces to reveal the bigger picture of your life.</p>
                  <h3>Easy game onboarding</h3>
                  <p>Start anywhere. Begin with one memory or a collection. Build backward, forward, or anywhere in between. The vision is to help you match people, places, dates, and historical events, discover missing pieces, and invite others to help complete your puzzle.</p>
                  <h3>Rewards &amp; SB Tokens</h3>
                  <p>Proposed internal game tokens would reward completing Memory Bits and making meaningful connections. These rewards are a future concept, separate from purchased AI credits and real-money affiliate commissions.</p>
                </details>
              </div>
            </div>
          </section>

          <section
            id="plans-earning"
            class="sb-landing-section sb-landing-section--tint"
            aria-labelledby="sb-plans-title"
          >
            <div class="sb-landing-container">
              <p class="sb-eyebrow">
                Plans &amp; earning
              </p>
              <h2 id="sb-plans-title">
                Your subscription builds more than your biography.
              </h2>
              <div class="sb-plans-intro">
                <p>Self Biography was created with a different idea about how a social platform can support itself. We want the people who benefit from Self Biography to help make it possible.</p>
                <p>Starting at {{ startingPrice }} a month, small contributions from a growing community help support storage, security, AI tools, development, and long-term preservation.</p>
                <p><strong>A little from each of us can build something substantial for all of us.</strong></p>
              </div>
              <div
                v-if="catalog"
                class="sb-plans-grid"
              >
                <section
                  v-for="plan in catalog.plans"
                  :key="plan.id"
                  class="sb-plan"
                  :class="{ 'sb-plan--personal': plan.id === 'personal' }"
                >
                  <h3>{{ plan.name }}</h3>
                  <p class="sb-plan-price">
                    {{ money(plan.amount, plan.currency) }}<span v-if="plan.amount"> / month</span>
                  </p>
                  <p>{{ planDescriptions[plan.id] }}</p>
                  <ul>
                    <li>{{ storage(plan.storageBytes) }} storage</li>
                    <li v-if="plan.creditPeriod === 'welcome'">
                      {{ plan.credits }} welcome AI credits, valid for 30 days
                    </li>
                    <li v-else>
                      {{ plan.credits.toLocaleString('en-US') }} AI credits each billing cycle
                    </li>
                  </ul>
                  <router-link
                    class="sb-landing-button"
                    :class="{ 'sb-landing-button--primary': plan.id === 'personal' }"
                    :to="plan.id === 'free' ? joinTarget : '/pages/pricing#choose-plan'"
                  >
                    {{ plan.id === 'free' ? 'Join free' : 'Explore plan' }}
                  </router-link>
                </section>
              </div>
              <p v-else-if="pricingUnavailable">
                Visit Plans &amp; Subscriptions for the current prices and allowances.
              </p>
              <p
                v-else
                role="status"
              >
                Loading current plans…
              </p>
              <p class="sb-plan-note">
                All plans include Personal and Professional biographies and privacy controls. Paid plans add capacity and AI credits. Monthly credits and purchased credits remain separate; purchased packs do not expire. Existing subscriptions keep their current prices and allowances until you choose to change plans.
              </p>
              <router-link
                class="sb-text-link"
                to="/pages/pricing"
              >
                Compare Plans &amp; Subscriptions →
              </router-link>
              <div class="sb-earning-intro">
                <div>
                  <h3>Help grow the community—and participate in its success.</h3>
                  <p>Your subscription helps support Self Biography. The Affiliate Program gives you an opportunity to participate in its growth. Introduce Self Biography to family, friends, professional networks, or your audience. Qualifying purchases may earn you a 25% real-money commission under the program terms.</p>
                </div>
                <router-link
                  class="sb-landing-button sb-landing-button--primary"
                  to="/pages/affiliate-program"
                >
                  Explore the Affiliate Program
                </router-link>
              </div>
            </div>
          </section>

          <section
            class="sb-landing-section"
            aria-labelledby="sb-coming-title"
          >
            <div class="sb-landing-container">
              <p class="sb-eyebrow">
                Coming soon &amp; future development
              </p>
              <h2 id="sb-coming-title">
                More chapters are still to come.
              </h2>
              <p class="sb-section-intro">
                The following ideas are in development or part of our future vision. They are separate from the features and subscription benefits available today.
              </p>
              <div class="sb-features-grid">
                <details
                  v-for="feature in content.planned"
                  :key="feature.title"
                  class="sb-feature sb-feature--planned"
                >
                  <summary>{{ feature.title }}<span aria-hidden="true">+</span></summary>
                  <p>{{ feature.text }}</p>
                </details>
              </div>
              <div class="sb-future-copy">
                <h3>Beyond the screen</h3>
                <p>Social technology should bring people closer together. As Self Biography evolves, we envision tools for family reunions, gatherings with friends, school reunions, professional meetings, and community events—turning online connections into real-world experiences and preserving them as new Memory Bits.</p>
                <p><strong>Connect online. Come together in life. Make new memories.</strong></p>
                <details class="sb-reading-more">
                  <summary>Building a community based on trust</summary>
                  <p>Today, Self Biography is about preserving lives, memories, relationships, and accomplishments. As the community grows, that foundation may also make commerce, creative collaboration, professional connections, and personal relationships possible.</p>
                  <p>A voluntarily shared biography can provide history and context while each member controls what remains private. Marketplace, commerce, and dating remain future concepts; biography history alone does not verify identity or guarantee trustworthiness.</p>
                </details>
              </div>
            </div>
          </section>

          <section
            class="sb-landing-section sb-closing"
            aria-labelledby="sb-start-title"
          >
            <div class="sb-landing-container">
              <p class="sb-eyebrow">
                Your story is worth preserving
              </p>
              <h2 id="sb-start-title">
                Start anywhere.<br />Start with one memory.
              </h2>
              <p>Bring people together through their shared memories rather than keep them apart by their opinions.</p>
              <router-link
                class="sb-landing-button sb-landing-button--primary"
                :to="joinTarget"
              >
                Join free
              </router-link>
            </div>
          </section>
        </article>
      </v-main>
    </v-app>
  </div>
</template>

<script>

// Keep the backup untouched, including its existing legacy CSS for shared layouts.
import LandingPageOriginal from './LandingPageOriginal.vue'
import content from '@/content/landingPageContent.json'
import billing from '@/services/billing'

export default {
  name: 'PublicLandingPage',
  components: { LandingPageOriginal },
  data: () => ({
    content,
    activeStory: 0,
    catalog: null,
    pricingUnavailable: false,
    motionPaused: false,
    planDescriptions: {
      free: 'Discover Self Biography and begin preserving your story.',
      personal: 'Preserve your memories, photographs, diary, and family history.',
      professional: 'Preserve your life and your life’s work: career, projects, and portfolio.',
      business_plus: 'More space and AI credits for professional history and a growing archive.',
    },
  }),
  computed: {
    showOriginal() { return this.$route.query.version === 'original' },
    joinTarget() { return this.$store.state.logged ? '/my-biography' : '/auth' },
    startingPrice() {
      const personal = this.catalog && this.catalog.plans.find(plan => plan.id === 'personal')

      return personal ? this.money(personal.amount, personal.currency) : '$2'
    },
  },
  watch: {
    showOriginal(value) {
      if (!value) this.prepareLanding()
    },
  },
  mounted() { if (!this.showOriginal) this.prepareLanding() },
  methods: {
    prepareLanding() {
      if (!this.catalog) {
        billing.catalog().then(catalog => { this.catalog = catalog; this.pricingUnavailable = false })
          .catch(() => { this.pricingUnavailable = true })
      }
      this.$nextTick(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches && this.$refs.heroVideo) {
          this.$refs.heroVideo.pause()
          this.motionPaused = true
        }
      })
    },
    money(amount, currency = 'usd') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency', currency, minimumFractionDigits: 0, maximumFractionDigits: 2,
      }).format(amount / 100)
    },
    storage(bytes) { return bytes < 1000000000 ? `${bytes / 1000000} MB` : `${bytes / 1000000000} GB` },
    toggleMotion() {
      const video = this.$refs.heroVideo
      if (video.paused) video.play().then(() => { this.motionPaused = false }).catch(() => {})
      else { video.pause(); this.motionPaused = true }
    },
    onStoryKey(event, index) {
      let next = index
      if (event.key === 'ArrowRight') next = (index + 1) % this.content.stories.length
      else if (event.key === 'ArrowLeft') next = (index - 1 + this.content.stories.length) % this.content.stories.length
      else if (event.key === 'Home') next = 0
      else if (event.key === 'End') next = this.content.stories.length - 1
      else return
      event.preventDefault()
      this.activeStory = next
      this.$nextTick(() => this.$refs.storyTabs[next].focus())
    },
  },
}
</script>

<style scoped lang="scss">
.sb-landing-version { min-width: 0; }
.sb-public-landing {
  --sb-text: #5e5669; --sb-heading: #393345; --sb-accent: #713ac7; --sb-surface: #fff; --sb-tint: #f7f3fc; --sb-line: #e6dfef;
  color: var(--sb-text); background: var(--sb-surface); font-size: 16px; line-height: 1.75; text-align: left;
  &--dark { --sb-text: #d5cfe2; --sb-heading: #f0ebf8; --sb-accent: #c6a5ff; --sb-surface: #28243d; --sb-tint: #312d4b; --sb-line: #514662; }
  h1, h2, h3 { text-align: left; word-break: normal; letter-spacing: -.025em; }
  h2 { color: var(--sb-heading); font-size: clamp(1.8rem, 3vw, 2.8rem); line-height: 1.22; font-weight: 500; margin: 0 0 24px; }
  h3 { color: var(--sb-heading); font-size: 1.2rem; line-height: 1.4; font-weight: 600; margin: 0 0 16px; }
  p { margin: 0 0 20px; }
  img { margin: 0; max-width: 100%; }
  a, button, summary { -webkit-tap-highlight-color: transparent; }
  a:focus-visible, button:focus-visible, summary:focus-visible, [role=tabpanel]:focus-visible { outline: 2px solid var(--sb-accent); outline-offset: 4px; }
}
.sb-landing-container { width: min(1180px, calc(100% - 64px)); margin: 0 auto; }
.sb-landing-section { padding: 88px 0; }
.sb-landing-section--tint { background: var(--sb-tint); }
.sb-public-landing .sb-eyebrow { color: var(--sb-accent); font-size: 12px; font-weight: 700; letter-spacing: .12em; text-transform: uppercase; margin-bottom: 16px; }
.sb-section-intro { max-width: 720px; margin-bottom: 32px !important; }
.sb-hero { position: relative; display: flex; align-items: center; min-height: 730px; overflow: hidden; background: #0c0b19; color: #fff; }
.sb-public-landing .sb-hero-video { position: absolute; inset: 0; width: 100%; height: 100%; min-height: 100%; max-height: none; object-fit: cover; }
.sb-hero-shade { position: absolute; inset: 0; background: linear-gradient(90deg, rgba(5,4,16,.91) 0%, rgba(5,4,16,.75) 48%, rgba(5,4,16,.18) 100%); }
.sb-hero-content { position: relative; z-index: 1; padding: 64px 0 84px; }
.sb-hero-brand { display: flex; align-items: center; gap: 16px; margin-bottom: 28px; }
.sb-public-landing .sb-hero-brand img { width: 56px; height: 56px; filter: brightness(0) invert(1); }
.sb-hero-brand > span { font-family: copperplatelight, sans-serif; font-size: 24px; letter-spacing: 1px; }
.sb-hero-brand > span > span { color: #b98dff; }
.sb-hero h1 { color: #fff; font-size: clamp(2.4rem, 4.3vw, 4.3rem); line-height: 1.12; font-weight: 500; max-width: 820px; margin-bottom: 26px; }
.sb-public-landing .sb-hero-intro { font-size: 19px; line-height: 1.65; max-width: 650px; color: #e9e3f3; }
.sb-hero-puzzle { font-weight: 600; color: #fff; }
.sb-hero-actions { display: flex; flex-wrap: wrap; gap: 12px; margin: 28px 0 16px; }
.sb-public-landing .sb-landing-button { display: inline-flex; align-items: center; justify-content: center; min-height: 46px; padding: 12px 22px; border: 1px solid var(--sb-line); border-radius: 5px; color: var(--sb-accent); font-size: 12px; font-weight: 700; letter-spacing: .07em; line-height: 1.4; text-align: center; text-decoration: none; text-transform: uppercase; transition: background-color .15s ease; }
.sb-public-landing .sb-landing-button:hover { background: var(--sb-tint); }
.sb-public-landing .sb-landing-button--primary { background: #713ac7; border-color: #713ac7; color: #fff; }
.sb-public-landing .sb-landing-button--primary:hover { background: #6030aa; }
.sb-public-landing .sb-landing-button--hero { border-color: rgba(255,255,255,.55); color: #fff; }
.sb-public-landing .sb-landing-button--hero:hover { background: rgba(255,255,255,.13); }
.sb-public-landing .sb-hero-note { font-size: 13px; color: #d8d1e5; margin: 0; max-width: 650px; }
.sb-motion-control { position: absolute; z-index: 2; bottom: 20px; right: 24px; color: #fff; font-size: 12px; padding: 8px 12px; border: 1px solid rgba(255,255,255,.4); border-radius: 4px; }
.sb-devices-layout { display: grid; grid-template-columns: 1fr 1.15fr; align-items: center; gap: 56px; }
.sb-public-landing .sb-device-image { width: 100%; height: auto; }
.sb-device-list { list-style: none; padding: 0; display: flex; flex-wrap: wrap; gap: 8px; margin-top: 24px; }
.sb-device-list li { font-size: 12px; padding: 3px 10px; border: 1px solid var(--sb-line); border-radius: 20px; }
.sb-benefits-grid { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 32px; margin-top: 40px; }
.sb-benefit { padding-top: 20px; border-top: 1px solid var(--sb-line); }
.sb-section-number { display: block; font-size: 14px; color: var(--sb-accent); margin-bottom: 20px; }
.sb-benefit p { font-size: 15px; margin: 0; }
.sb-story-tabs { display: flex; gap: 8px; overflow-x: auto; padding: 4px 4px 16px; margin: 0 -4px 24px; scrollbar-width: thin; }
.sb-story-tab { flex: 0 0 auto; padding: 10px 14px; border: 1px solid var(--sb-line); border-radius: 5px; color: var(--sb-text); font-size: 13px; }
.sb-story-tab[aria-selected=true] { background: var(--sb-tint); border-color: var(--sb-accent); color: var(--sb-accent); font-weight: 600; }
.sb-story-panel { display: grid; grid-template-columns: 1.1fr 1fr; align-items: start; gap: 48px; }
.sb-story-copy h3 { font-size: 1.65rem; }
.sb-public-landing .sb-story-image { width: 100%; height: 340px; object-fit: cover; border-radius: 8px; }
.sb-public-landing .sb-text-link { color: var(--sb-accent); font-weight: 600; text-decoration: none; display: inline-flex; align-items: center; min-height: 44px; margin-top: 12px; }
.sb-text-link:hover { text-decoration: underline; text-underline-offset: 4px; }
.sb-features-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 12px 24px; margin-bottom: 20px; align-items: start; }
.sb-feature { border: 1px solid var(--sb-line); border-radius: 6px; background: var(--sb-surface); }
.sb-feature summary { min-height: 68px; padding: 18px 20px; display: flex; align-items: center; justify-content: space-between; gap: 12px; list-style: none; color: var(--sb-heading); font-weight: 600; line-height: 1.5; cursor: pointer; }
.sb-feature summary::-webkit-details-marker { display: none; }
.sb-feature summary span { color: var(--sb-accent); font-size: 24px; font-weight: 400; }
.sb-feature[open] summary span { transform: rotate(45deg); }
.sb-feature p { font-size: 15px; padding: 0 20px 20px; margin: 0; }
.sb-feature--planned { background: var(--sb-tint); }
.sb-reading-more { border-top: 1px solid var(--sb-line); margin-top: 24px; padding-top: 12px; }
.sb-reading-more summary { cursor: pointer; color: var(--sb-accent); font-weight: 600; padding: 10px 0; }
.sb-reading-more[open] summary { margin-bottom: 12px; }
.sb-puzzle-layout { display: grid; grid-template-columns: 1fr 1.2fr; align-items: start; gap: 64px; }
.sb-puzzle-picture { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; padding: 10px; background: var(--sb-tint); border-radius: 12px; transform: rotate(-3deg); margin-top: 36px; }
.sb-puzzle-piece { position: relative; overflow: hidden; border-radius: 6px; min-width: 0; }
.sb-public-landing .sb-puzzle-piece img { display: block; width: 100%; height: 170px; object-fit: cover; }
.sb-puzzle-piece span { position: absolute; left: 0; right: 0; bottom: 0; padding: 24px 12px 8px; color: #fff; background: linear-gradient(transparent,rgba(0,0,0,.8)); font-size: 12px; }
.sb-puzzle-statement { font-weight: 600; color: var(--sb-heading); }
.sb-game-details h3 { margin-top: 24px; }
.sb-public-landing .sb-status-label { font-size: 12px; font-weight: 600; text-transform: uppercase; letter-spacing: .06em; color: var(--sb-accent); }
.sb-plans-intro { max-width: 850px; }
.sb-plans-grid { display: grid; grid-template-columns: repeat(4,minmax(0,1fr)); gap: 16px; margin: 40px 0 24px; }
.sb-plan { display: flex; flex-direction: column; padding: 24px 20px; border: 1px solid var(--sb-line); border-radius: 7px; background: var(--sb-surface); }
.sb-plan--personal { border-color: var(--sb-accent); }
.sb-public-landing .sb-plan-price { font-size: 34px; line-height: 1.4; font-weight: 500; color: var(--sb-heading); margin: 0 0 20px; }
.sb-plan-price span { font-size: 12px; font-weight: 400; color: var(--sb-text); white-space: nowrap; }
.sb-plan > p:not(.sb-plan-price), .sb-plan li { font-size: 14px; }
.sb-plan ul { padding-left: 18px; margin-bottom: 24px; }
.sb-plan .sb-landing-button { margin-top: auto; }
.sb-public-landing .sb-plan-note { font-size: 13px; max-width: 930px; }
.sb-earning-intro { display: grid; grid-template-columns: 1fr auto; align-items: center; gap: 32px; border-top: 1px solid var(--sb-line); margin-top: 36px; padding-top: 36px; }
.sb-earning-intro p { max-width: 760px; margin-bottom: 0; }
.sb-future-copy { max-width: 850px; margin-top: 40px; }
.sb-closing { background: var(--sb-tint); }
.sb-closing p { max-width: 680px; }
@media(max-width:959px) {
  .sb-landing-section { padding: 64px 0; }
  .sb-devices-layout, .sb-story-panel, .sb-puzzle-layout { grid-template-columns: 1fr; gap: 32px; }
  .sb-device-image { max-width: 700px !important; margin: 0 auto !important; }
  .sb-benefits-grid { gap: 20px; }
  .sb-benefit h3 { font-size: 1.1rem; }
  .sb-plans-grid { grid-template-columns: repeat(2,minmax(0,1fr)); }
  .sb-puzzle-picture { max-width: 550px; margin: 0 12px; }
  .sb-earning-intro { grid-template-columns: 1fr; gap: 20px; }
  .sb-earning-intro .sb-landing-button { justify-self: start; }
}
@media(max-width:599px) {
  .sb-landing-container { width: calc(100% - 40px); }
  .sb-landing-section { padding: 48px 0; }
  .sb-hero { min-height: 0; }
  .sb-hero-content { padding: 40px 0 68px; }
  .sb-hero-brand { gap: 12px; margin-bottom: 24px; }
  .sb-hero-brand > span { font-size: 20px; }
  .sb-public-landing .sb-hero-brand img { width: 42px; height: 42px; }
  .sb-hero h1 { font-size: clamp(2rem, 8.9vw, 2.7rem); line-height: 1.16; }
  .sb-public-landing .sb-hero-intro { font-size: 16px; }
  .sb-hero-actions { display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); gap: 8px; }
  .sb-hero-actions .sb-landing-button { padding: 12px 8px; font-size: 11px; }
  .sb-motion-control { right: 16px; bottom: 14px; padding: 6px 8px; font-size: 11px; }
  .sb-benefits-grid, .sb-features-grid { grid-template-columns: 1fr; }
  .sb-benefits-grid { margin-top: 20px; gap: 24px; }
  .sb-benefit p, .sb-feature p { font-size: 15px; }
  .sb-public-landing .sb-story-image { height: 250px; }
  .sb-story-copy h3 { font-size: 1.4rem; }
  .sb-plans-grid { grid-template-columns: 1fr; margin-top: 28px; }
  .sb-plan { padding: 24px; }
  .sb-public-landing .sb-puzzle-piece img { height: 135px; }
  .sb-earning-intro .sb-landing-button { justify-self: stretch; }
}
@media(prefers-reduced-motion:reduce) {
  .sb-public-landing *, .sb-public-landing *::before, .sb-public-landing *::after { transition: none !important; scroll-behavior: auto !important; }
}
</style>
